/**
 * http-rpc.ts — a Nakama RPC over plain HTTP with the server's http key: for
 * what runs before a player has a session — an invite link, a team code
 * (domains/sessions.ts) — and for the admin's save manager, which lists and
 * deletes the saves on the local server (domains/local-saves.ts). Uses
 * unwrap=true, so the payload and the answer are plain JSON.
 *
 * A refusal says the server's own words: Nakama sends one as JSON, its
 * English in `message` (runtime.NewError). A body with none — a gateway's
 * page, or nothing, while Nakama restarts behind its proxy — carries no words
 * of the server's: serverMessage reads it as null, here and wherever a refused
 * call is read (domains/join-refusals.ts), so the game not reached is never
 * said as a status line.
 */

import { getCurrentConfig } from '../../config/nakama.config';

/** The server's own words in a refused call's body: Nakama's `message`, or null when the body carries none. */
export function serverMessage(body: string): string | null {
  try {
    const refusal: unknown = JSON.parse(body);
    if (refusal && typeof refusal === 'object' && 'message' in refusal && typeof refusal.message === 'string') {
      return refusal.message;
    }
  } catch {
    // Not JSON: a gateway's page, plain text, or nothing.
  }
  return null;
}

/**
 * A refused RPC: its message says which RPC and how, for the log, and the
 * server's own words are kept apart — a refused join's are its code, which
 * the way in says in the player's language (domains/join-refusals.ts) — or
 * null when its body carried none.
 */
export class RpcRefusal extends Error {
  readonly status: number;
  readonly words: string | null;

  constructor(rpcId: string, status: number, body: string) {
    const words = serverMessage(body);
    super(`RPC ${rpcId} failed: ${status} - ${words ?? body}`);
    this.name = 'RpcRefusal';
    this.status = status;
    this.words = words;
  }
}

/** Calls an RPC and resolves with its answer, or rejects with an RpcRefusal: `RPC <id> failed: <status> - <the server's words>`. */
export async function callRpcHttp<T = unknown>(rpcId: string, payload: unknown = {}): Promise<T> {
  const config = getCurrentConfig();
  const protocol = config.useSSL ? 'https' : 'http';
  const url = `${protocol}://${config.host}:${config.port}/v2/rpc/${rpcId}?http_key=${config.httpKey}&unwrap=true`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new RpcRefusal(rpcId, response.status, await response.text());
  }
  return (await response.json()) as T;
}
